import * as Sentry from '@sentry/react';
import { errorNotification } from 'components/ToastNotifications';
import env from './env';
import { get } from './http';
import L10n from './L10n';
import { L10Common } from './l10n/L10nCommon';
import isLoggedIn from './requireAuth';

const HUBSPOT_CONTAINER_ID = 'hubspot-messages-iframe-container';
const DISMISS_BUTTON_ID = 'avoma-hubspot-chat-dismiss';
const HUBSPOT_SHADOW_CONTAINER_CLASS = 'hs-shadow-container';
const HUBSPOT_FORCE_DISMISS_DATA_ATTR = 'avomaForceDismissWidget';
const HUBSPOT_FORCE_DISMISS_ATTRIBUTE = 'data-avoma-force-dismiss-widget';
const WIDGET_HIDE_DELAY_MS = 5000;
const HUBSPOT_LOAD_GRACE_PERIOD_DELAY_MS = 750;
const MAX_INITIAL_DISMISS_RAF_ATTEMPTS = 300;
const HUBSPOT_EXPANDED_WIDGET_HEIGHT_THRESHOLD = 160;

const DISMISS_WIDE_CLASS = 'is-wide';
const USE_WIDE_DISMISS_PLACEMENT =
  env.name === 'prod' || env.name === 'staging';

const getHubspotContainer = () => document.getElementById(HUBSPOT_CONTAINER_ID);

const hideDismissButton = () => {
  document.getElementById(DISMISS_BUTTON_ID)?.classList.remove('visible');
};

const dismissMinimizedWidget = hubspotContainer => {
  hubspotContainer.classList.remove('show');
};

const hideHubspotWidget = hubspotContainer => {
  hubspotContainer?.removeAttribute?.(HUBSPOT_FORCE_DISMISS_ATTRIBUTE);
  hubspotContainer?.classList?.remove('show');
  hubspotContainer?.classList?.add('hide');
  shouldForceDismissWidget = false;
};

const shouldForceDismissHubspotWidget = hubspotContainer =>
  Boolean(
    shouldForceDismissWidget ||
      hubspotContainer?.dataset?.[HUBSPOT_FORCE_DISMISS_DATA_ATTR] === 'true'
  );

const isHubspotWidgetExpanded = hubspotContainer => {
  const iframe = hubspotContainer?.querySelector('iframe');
  if (!iframe?.getBoundingClientRect) return false;

  return (
    iframe.getBoundingClientRect().height >
    HUBSPOT_EXPANDED_WIDGET_HEIGHT_THRESHOLD
  );
};

const isHubspotChatActive = () => {
  const chatContainer = document.getElementsByClassName(
    HUBSPOT_SHADOW_CONTAINER_CLASS
  )[0];
  return Boolean(chatContainer && chatContainer.classList.contains('active'));
};

const ensureDismissButton = (hubspotContainer, onDismiss) => {
  let dismissButton = document.getElementById(DISMISS_BUTTON_ID);

  if (!dismissButton) {
    dismissButton = document.createElement('button');
    dismissButton.id = DISMISS_BUTTON_ID;
    dismissButton.type = 'button';
    dismissButton.style.setProperty('cursor', 'pointer', 'important');
    if (USE_WIDE_DISMISS_PLACEMENT) {
      dismissButton.classList.add(DISMISS_WIDE_CLASS);
    }
    dismissButton.setAttribute('aria-label', L10Common.close);
    dismissButton.innerHTML = '&times;';
    dismissButton.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      onDismiss();
    });
    hubspotContainer.appendChild(dismissButton);
  }

  return dismissButton;
};

const showDismissButton = (hubspotContainer, onDismiss) => {
  const dismissButton = ensureDismissButton(hubspotContainer, onDismiss);
  dismissButton.classList.add('visible');
};

const createGracePeriodController = () => {
  let closeTimeOut;
  let dismissResizeObserver;
  let chatContainerMutationObserver;

  const clearGracePeriod = () => {
    if (closeTimeOut) {
      clearTimeout(closeTimeOut);
      closeTimeOut = undefined;
    }
    dismissResizeObserver?.disconnect();
    dismissResizeObserver = undefined;
    chatContainerMutationObserver?.disconnect();
    chatContainerMutationObserver = undefined;
    hideDismissButton();
  };

  const watchForChatReopen = hubspotContainer => {
    dismissResizeObserver?.disconnect();
    chatContainerMutationObserver?.disconnect();

    const chatContainer = document.getElementsByClassName(
      HUBSPOT_SHADOW_CONTAINER_CLASS
    )[0];
    if (chatContainer && typeof MutationObserver !== 'undefined') {
      chatContainerMutationObserver = new MutationObserver(() => {
        if (chatContainer.classList.contains('active')) {
          clearGracePeriod();
        }
      });
      chatContainerMutationObserver.observe(chatContainer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    const iframe = hubspotContainer.querySelector('iframe');
    if (!iframe || typeof ResizeObserver === 'undefined') {
      return;
    }

    const startObserving = () => {
      const baselineHeight = iframe.getBoundingClientRect().height;

      dismissResizeObserver = new ResizeObserver(entries => {
        const height = entries[0]?.contentRect.height ?? 0;
        // Reopening the chat expands the iframe beyond the minimized launcher.
        if (height > baselineHeight + 40) {
          clearGracePeriod();
        }
      });
      dismissResizeObserver.observe(iframe);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(startObserving);
    });
  };

  const startGracePeriod = (
    hubspotContainer,
    onDismiss,
    { forceDismissWidget = false } = {}
  ) => {
    clearGracePeriod();
    showDismissButton(hubspotContainer, onDismiss);
    watchForChatReopen(hubspotContainer);

    closeTimeOut = setTimeout(() => {
      closeTimeOut = undefined;
      dismissResizeObserver?.disconnect();
      dismissResizeObserver = undefined;
      hideDismissButton();
      if (forceDismissWidget) {
        onDismiss();
        return;
      }
      const [chatContainer] = document.getElementsByClassName(
        HUBSPOT_SHADOW_CONTAINER_CLASS
      );
      if (!(chatContainer && chatContainer.classList.contains('active'))) {
        hubspotContainer.classList.remove('show');
      }
    }, WIDGET_HIDE_DELAY_MS);
  };

  return {
    clearGracePeriod,
    startGracePeriod
  };
};

const hubspotGracePeriodController = createGracePeriodController();
let shouldForceDismissWidget = false;
let pendingInitialDismissTimeout;
let pendingInitialDismissRafId;
let pendingInitialDismissRafAttempts = 0;
let shouldSkipInitialDismiss = false;
let widgetVisibilityHandlerWrapper;

const clearPendingInitialDismissPolling = () => {
  if (pendingInitialDismissRafId !== undefined) {
    try {
      cancelAnimationFrame?.(pendingInitialDismissRafId);
    } catch {
      clearTimeout(pendingInitialDismissRafId);
    }
    pendingInitialDismissRafId = undefined;
  }
  pendingInitialDismissRafAttempts = 0;
};

const resetPendingInitialDismissState = () => {
  clearPendingInitialDismissPolling();

  if (pendingInitialDismissTimeout) {
    clearTimeout(pendingInitialDismissTimeout);
    pendingInitialDismissTimeout = undefined;
  }

  shouldForceDismissWidget = false;
  shouldSkipInitialDismiss = false;
};

const startDismissGracePeriod = ({
  forceDismissWidget,
  hubspotContainer = getHubspotContainer()
} = {}) => {
  if (!hubspotContainer) return;

  const shouldForceDismissWidgetOnTimeout =
    forceDismissWidget ?? shouldForceDismissHubspotWidget(hubspotContainer);

  hubspotGracePeriodController.startGracePeriod(
    hubspotContainer,
    () => {
      hubspotGracePeriodController.clearGracePeriod();
      if (shouldForceDismissWidgetOnTimeout) {
        hideHubspotWidget(hubspotContainer);
        return;
      }
      dismissMinimizedWidget(hubspotContainer);
    },
    {
      forceDismissWidget: shouldForceDismissWidgetOnTimeout
    }
  );
};

const startDismissGracePeriodWhenReady = () => {
  const hubspotContainer = getHubspotContainer();
  if (!hubspotContainer) {
    if (pendingInitialDismissRafAttempts >= MAX_INITIAL_DISMISS_RAF_ATTEMPTS) {
      resetPendingInitialDismissState();
      return;
    }

    pendingInitialDismissRafAttempts += 1;
    pendingInitialDismissRafId = requestAnimationFrame(
      startDismissGracePeriodWhenReady
    );
    return;
  }

  clearPendingInitialDismissPolling();
  shouldSkipInitialDismiss = false;
  shouldForceDismissWidget = true;
  widgetVisibilityHandlerWrapper();
  pendingInitialDismissTimeout = window.setTimeout(() => {
    const refreshedHubspotContainer = getHubspotContainer();
    if (!refreshedHubspotContainer) {
      resetPendingInitialDismissState();
      return;
    }

    pendingInitialDismissTimeout = undefined;

    if (shouldSkipInitialDismiss) {
      return;
    }

    if (isHubspotWidgetExpanded(refreshedHubspotContainer)) {
      return;
    }

    refreshedHubspotContainer.dataset[HUBSPOT_FORCE_DISMISS_DATA_ATTR] = 'true';
    refreshedHubspotContainer.classList.add('show');

    hubspotGracePeriodController.startGracePeriod(
      refreshedHubspotContainer,
      () => {
        hubspotGracePeriodController.clearGracePeriod();
        hideHubspotWidget(refreshedHubspotContainer);
      },
      {
        forceDismissWidget: true
      }
    );
  }, HUBSPOT_LOAD_GRACE_PERIOD_DELAY_MS);
};

// Returns a function that will show the widget when called
// This wrapper function holds the closeTimeOut and listenerAdded variables
// to make sure the listener is only added once
// and the timeout is cleared only when the widget is closed
const addWidgetClass = () => {
  const hubspotContainer = getHubspotContainer();

  if (!hubspotContainer) {
    requestAnimationFrame(addWidgetClass);
    return;
  }

  hubspotContainer?.classList?.add('hide');
};

const queueHubspotOnReadyCallback = callback => {
  if (Array.isArray(window.hsConversationsOnReady)) {
    window.hsConversationsOnReady.push(callback);
    return;
  }

  window.hsConversationsOnReady = [callback];
};

const loadWidget = () => {
  /*
     If external API methods are already available, use them.
    */
  if (window.HubSpotConversations) {
    const hubspotWidgetStatus =
      window?.HubSpotConversations?.widget?.status?.();
    if (!hubspotWidgetStatus?.loaded) {
      window.HubSpotConversations?.widget?.load();
    }
    addWidgetClass();
  } else {
    /*
          Otherwise, callbacks can be added to the hsConversationsOnReady on the window object.
          These callbacks will be called once the external API has been initialized.
        */
    queueHubspotOnReadyCallback(loadWidget);
  }
};

window.hsConversationsSettings = {
  loadImmediately: false
};

const widgetVisibilityHandler = () => {
  let listenerAdded = false;

  return () => {
    const hubspotContainer = getHubspotContainer();
    if (!hubspotContainer) return;

    hubspotGracePeriodController.clearGracePeriod();
    hubspotContainer?.classList?.add('show');

    if (listenerAdded) return;

    listenerAdded = true;

    // HubSpot does not always emit widgetOpened when the launcher is clicked.
    window.HubSpotConversations.on('widgetOpened', () => {
      const hasPendingInitialDismiss = Boolean(pendingInitialDismissTimeout);
      shouldSkipInitialDismiss = true;
      if (pendingInitialDismissTimeout) {
        clearTimeout(pendingInitialDismissTimeout);
        pendingInitialDismissTimeout = undefined;
      }
      if (!hasPendingInitialDismiss) {
        shouldForceDismissWidget = false;
      }
      hubspotGracePeriodController.clearGracePeriod();
    });

    // Show the widget when widget is closed and conversation is started by Avoma team.
    window.HubSpotConversations.on('unreadConversationCountChanged', () => {
      const refreshedHubspotContainer = getHubspotContainer();
      refreshedHubspotContainer?.classList?.add('show');
      if (isHubspotChatActive()) {
        hubspotGracePeriodController.clearGracePeriod();
        return;
      }
      startDismissGracePeriod({ hubspotContainer: refreshedHubspotContainer });
    });

    // Hiding the widget when the user closes it after 5 seconds basically graceful exit.
    // Keep `widgetClosed` on the unconditional path: HubSpot fires it before
    // `.hs-shadow-container` drops `.active`, so routing this through the
    // unread-active guard would prevent the dismiss button from ever being
    // created during the real close flow.
    window.HubSpotConversations.on('widgetClosed', () => {
      const refreshedHubspotContainer = getHubspotContainer();
      startDismissGracePeriod({ hubspotContainer: refreshedHubspotContainer });
    });
  };
};

widgetVisibilityHandlerWrapper = widgetVisibilityHandler();

const resetHubspotLiveChatStateForTests = () => {
  hubspotGracePeriodController.clearGracePeriod();
  resetPendingInitialDismissState();
  widgetVisibilityHandlerWrapper = widgetVisibilityHandler();
};

export const openHubspotChat = () => {
  let readyRafId;
  let readyAttempts = 0;

  const clearReadyPolling = () => {
    if (readyRafId !== undefined) {
      try {
        cancelAnimationFrame?.(readyRafId);
      } catch {
        clearTimeout(readyRafId);
      }
      readyRafId = undefined;
    }
    readyAttempts = 0;
  };

  function onConversationsAPIReady() {
    const hubspotWidgetStatus =
      window?.HubSpotConversations?.widget?.status?.();
    if (!hubspotWidgetStatus?.loaded) {
      if (readyAttempts >= MAX_INITIAL_DISMISS_RAF_ATTEMPTS) {
        clearReadyPolling();
        const error = new Error('HubSpot chat did not finish loading');
        Sentry.captureException(error, 'Hubspot Chat loading failed');
        errorNotification({
          message:
            L10n?.views?.error ?? 'Something went wrong. Please try again later.'
        });
        return;
      }

      readyAttempts += 1;
      readyRafId = requestAnimationFrame(onConversationsAPIReady);
      return;
    }

    clearReadyPolling();

    try {
      // Needs to reset and reload since clicking on the button for Contact Us again
      // hides the chat for an amount of time, requiring a refresh to happen before
      // its accessible
      // window.HubSpotConversations.resetAndReloadWidget();
      window.HubSpotConversations.widget.open();

      // Hubspot widget is always visible, so we need to hide it
      // and show it when the user clicks on the button
      widgetVisibilityHandlerWrapper();
    } catch (e) {
      Sentry.captureException(e, 'Hubspot Chat loading failed');
      errorNotification({
        message:
          L10n?.views?.error ?? 'Something went wrong. Please try again later.'
      });
    }
  }
  /*
     If external API methods are already available, use them.
    */
  if (window.HubSpotConversations) {
    if (!window.HubSpotConversations?.widget?.status?.()?.loaded) {
      loadWidget();
    }
    onConversationsAPIReady();
  } else {
    queueHubspotOnReadyCallback(() => {
      loadWidget();
      onConversationsAPIReady();
    });
  }
};

export const generateHubspotVisitorIdentificationId = async () => {
  if (!isLoggedIn()) {
    return;
  }

  try {
    const hubspotStatus = window?.HubSpotConversations?.widget?.status?.();

    if (!hubspotStatus?.loaded) {
      const response = await get(
        `${env.avomaApiUrl}/api/v1/hubspot/visitor_identification_token/`
      );
      window.hsConversationsSettings = {
        identificationEmail: response?.user?.email ?? '',
        identificationToken: response?.token ?? '',
        loadImmediately: false
      };
      loadWidget();
      return;
    }

    // Only overlay the dismiss state for an already-loaded launcher. Fresh
    // widget loads should stay on HubSpot's normal render path so we don't
    // force the launcher open on every page load.
    startDismissGracePeriodWhenReady();
  } catch (error) {
    Sentry.captureException('Error creating Hubspot visitor token:', error);
  }
};

export const removeHubspotChatOnLogout = () => {
  resetPendingInitialDismissState();
  window?.HubSpotConversations?.widget?.remove();
};

export const hubspotLiveChatTestUtils = {
  HUBSPOT_CONTAINER_ID,
  DISMISS_BUTTON_ID,
  WIDGET_HIDE_DELAY_MS,
  DISMISS_WIDE_CLASS,
  USE_WIDE_DISMISS_PLACEMENT,
  dismissMinimizedWidget,
  hideDismissButton,
  ensureDismissButton,
  showDismissButton,
  createGracePeriodController,
  widgetVisibilityHandler,
  startDismissGracePeriod,
  MAX_INITIAL_DISMISS_RAF_ATTEMPTS,
  resetHubspotLiveChatStateForTests
};
